/**
 * AskOverlay CSS Styles
 *
 * CSS-in-JS styles injected once on first component instantiation.
 * Scoped via .sm-ask-* prefix, same singleton injectStyles() convention
 * as GeographyModal/MarketTrendsChart/NeighborhoodModal (not MarketStats'
 * per-mount inline <style> tag - that's a different, less common
 * convention in this package). Overlay fade/slide transition is copied
 * directly from GeographyModal's styles.ts (.sm-geography-overlay), same
 * mechanics, new prefix.
 */
let stylesInjected = false;
const STYLES = `
.sm-ask-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 8vh 20px 20px;
  z-index: 1200;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
}

.sm-ask-overlay.active {
  opacity: 1;
  visibility: visible;
}

.sm-ask-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  transform: translateY(-12px);
  transition: transform 0.2s;
  overflow: hidden;
}

.sm-ask-overlay.active .sm-ask-modal {
  transform: translateY(0);
}

.sm-ask-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.sm-ask-input-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: #7c3aed;
}

.sm-ask-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.05rem;
  font-family: inherit;
  color: #111827;
}

.sm-ask-input::placeholder {
  color: #9ca3af;
}

.sm-ask-close {
  border: none;
  background: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}

.sm-ask-close:hover {
  color: #374151;
}

.sm-ask-body {
  overflow-y: auto;
  padding: 16px 20px 20px;
}

/* Empty state / example queries */

.sm-ask-examples-label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9ca3af;
  margin: 4px 0 10px;
}

.sm-ask-example-btn {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  color: #374151;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.12s ease;
}

.sm-ask-example-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

/* Loading state */

.sm-ask-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 4px;
  color: #6b7280;
  font-size: 0.92rem;
}

.sm-ask-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: sm-ask-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes sm-ask-spin {
  to { transform: rotate(360deg); }
}

/* Clarifying question */

.sm-ask-clarifying {
  padding: 14px 16px;
  margin-bottom: 16px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  color: #92400e;
  font-size: 0.92rem;
}

/* Error state */

.sm-ask-error {
  padding: 14px 16px;
  margin-bottom: 16px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 0.9rem;
}

.sm-ask-retry-btn {
  margin-top: 8px;
  border: 1px solid #991b1b;
  background: white;
  color: #991b1b;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 0.85rem;
  cursor: pointer;
}

/* Results */

.sm-ask-summary {
  font-size: 0.95rem;
  line-height: 1.5;
  color: #1f2937;
  margin-bottom: 12px;
}

.sm-ask-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}

.sm-ask-chip {
  font-size: 0.78rem;
  font-weight: 500;
  color: #5b21b6;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 999px;
  padding: 3px 10px;
}

.sm-ask-show-on-map-btn {
  display: block;
  width: 100%;
  padding: 11px 16px;
  border: none;
  border-radius: 8px;
  background: #7c3aed;
  color: white;
  font-size: 0.92rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease;
}

.sm-ask-show-on-map-btn:hover {
  background: #6d28d9;
}

.sm-ask-empty-note {
  color: #6b7280;
  font-size: 0.88rem;
}

/* Mobile: full-screen takeover once results are showing */
@media (max-width: 640px) {
  .sm-ask-overlay {
    padding: 0;
    align-items: stretch;
  }

  .sm-ask-overlay.sm-ask-has-results .sm-ask-modal {
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
  }
}
`;
export function injectStyles() {
    if (stylesInjected)
        return;
    const styleEl = document.createElement('style');
    styleEl.id = 'sm-ask-overlay-styles';
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
    stylesInjected = true;
}
//# sourceMappingURL=styles.js.map